/**
 * useWorkOrderWorkflowSync — React port of propertySelectedService.storeOrder
 * lines 287-313. Whenever the current work-order's `orderNumber` changes,
 * fetch the financial workflow state, vendor financial workflow state, AND
 * the work-order phase and push them into the app-globals store.
 *
 * In AngularJS these globals are populated by:
 *   workflowDataService.getCurrentFinancialWorkflowState(orderNumber)
 *     → $rootScope.G_WORKORDER_FINANCIAL_WORKFLOW
 *   voidDataService.GetVendorFinancialWorkflowState(orderNumber)
 *     → $rootScope.G_WORKORDER_VENDOR_WORKFLOW
 *   generalDataService.getPhaseWithOrderNumber(orderNumber)
 *     → $rootScope.WORK_ORDER_PHASE
 *
 * When React handles work-order routing (SearchWidget, PropertyRecordRoute,
 * useWorkOrderQueryHydration), the AngularJS storeOrder() never runs, so
 * these globals stay null. This hook fills the gap for both embedded and
 * standalone modes.
 *
 * WHY WORK_ORDER_PHASE matters: `useAuditPermissions` gates three audit-edit
 * flags on `phase === 'PHASEV'`. Without this fetch, those flags are always
 * false in standalone, which silently hides the Take Action dropdown on
 * results-page tasks (and any other UI that depends on the post-audit-edit
 * flags). See results.html line 80 and the `TakeActionDropdown` render gate.
 */

import { useEffect } from 'react';
import { axiosClient } from '@/lib/axiosClient';
import { buildApiBase } from '@/lib/buildApiBase';
import { usePropertyStore } from '@/store/propertyStore';
import { useAppGlobalsStore } from '@/store/appGlobalsStore';
import type { FinancialWorkflow, VendorWorkflow } from '@/store/appGlobalsStore';

const WORKFLOW_API = `${buildApiBase()}/API/Workflow`;
const VOID_API = `${buildApiBase()}/API/Void`;
const SUBMISSION_API = `${buildApiBase()}/API/Submission`;

export function useWorkOrderWorkflowSync(): void {
  const orderNumber = usePropertyStore((s) => s.workOrder?.orderNumber);
  const setFinancialWorkflow = useAppGlobalsStore((s) => s.setFinancialWorkflow);
  const setVendorWorkflow = useAppGlobalsStore((s) => s.setVendorWorkflow);
  const setWorkOrderPhase = useAppGlobalsStore((s) => s.setWorkOrderPhase);

  useEffect(() => {
    if (orderNumber == null) {
      setFinancialWorkflow(null);
      setVendorWorkflow(null);
      setWorkOrderPhase(null);
      return;
    }

    // Match AngularJS line 289: `$rootScope.G_WORKORDER_FINANCIAL_WORKFLOW = null`
    // before kicking off the fetch so stale data from a previous work order
    // doesn't render briefly while the new request is in flight.
    setFinancialWorkflow(null);
    setWorkOrderPhase(null);

    let cancelled = false;
    const order = String(orderNumber);

    axiosClient
      .get<FinancialWorkflow>(`${WORKFLOW_API}/GetCurrentWorkflowState`, {
        params: { orderNumber: order, flowName: 'WorkOrder Financials' },
      })
      .then((response) => {
        if (!cancelled) setFinancialWorkflow(response.data ?? null);
      })
      .catch(() => {
        // graceful — leave null on failure
      });

    axiosClient
      .get<VendorWorkflow>(`${VOID_API}/GetVendorFinancialWorkflowState`, {
        params: { orderNumber: order },
      })
      .then((response) => {
        if (!cancelled) setVendorWorkflow(response.data ?? null);
      })
      .catch(() => {
        // graceful — leave null on failure
      });

    // Phase fetch — required by `useAuditPermissions` for the post-audit
    // edit flags (`canEditAuditedAndPreInvoicedOrder`, `canEditAudited
    // AndVoidedOrder`, `canEditVendorAuditedAndVoidedOrder`). All three
    // require `phase === 'PHASEV'`. Matches propertySelectedService.js:307-313.
    axiosClient
      .get<string>(`${SUBMISSION_API}/GetPhaseWithOrderNumber`, {
        params: { orderNumber: order },
      })
      .then((response) => {
        if (!cancelled) setWorkOrderPhase(response.data ?? null);
      })
      .catch(() => {
        // graceful — leave null on failure
      });

    return () => {
      cancelled = true;
    };
  }, [orderNumber, setFinancialWorkflow, setVendorWorkflow, setWorkOrderPhase]);
}
